<template>
    <div class="login-page">
        <div class="login-container">
            <div class="login-panel">
                <img src="../../assets/images/navitas_logo_white.png" alt="Navitas Safetey Group">
                <text-input autocomplete="username email" name="email" label="E-mail / Username" hint="If you confirmed email, login with it, not username."
                            :init-value="loginEmail" ref="username" @keyup.enter.native="actionLogin"/>
                <password-input autocomplete="current-password" name="password" label="Password" ref="password" @keyup.enter.native="actionLogin"
                               :has-error="loginError" :error-message="loginErrorMessage"/>

                <text-input label="2 Factor Authentication Code" hint="Enter the code provided from your google authenticator app."
                            ref="code" @keyup.enter.native="actionLogin" v-if="showFACode" />

                <text-button label="Sign in" :loading-state="loadingState" @click="actionLogin"/>
                <div style="display: flex; align-items: stretch;">
                    <div class="login-link">
                        <a @click="actionFederatedEmailLogin">Sign in Using SSO</a>
                    </div>
                    <div class="forgot-password-link">
                        <a @click="actionForgotPassword">Forgot your password?</a>
                    </div>
                </div>
            </div>
            <login-notice />
        </div>

        <div v-if="showFederationDisabledNotice" class="sso-notice-below">
            <federation-notice-content @forgot-password="actionForgotPassword" />
        </div>
    </div>
</template>
<style>
.md-color-orange {
    color: #f1934b;
}
</style>
<script>
import TextInput from "../components/TextInput";
import PasswordInput from "../components/PasswordInput";
import TextButton from "../components/TextButton";
import {mapMutations} from "vuex";
import AuthService from "../../new/services/AuthService";
import LoginNotice from "../components/LoginNotice";
import FederationNoticeContent from "../components/FederationNoticeContent";

export default {

    name: "Login",
    components: {
        FederationNoticeContent,
        LoginNotice,
        TextButton,
        PasswordInput,
        TextInput,
    },
    data() {
        return {
            loadingState: false,
            showFACode: false,
            loginError: false,
            loginErrorMessage: '',
            failedAttempts: 0,
            maxFailedAttempts: 3,
        };
    },
    computed: {
        loginEmail() {
            return this.$route.query.email ?? '';
        },
        showFederationDisabledNotice() {
            return this.$route.query.federationDisabled === '1';
        },
    },
    methods: {
        actionLogin() {
            // Clear previous errors
            this.loginError = false;
            this.loginErrorMessage = '';

            this.loadingState = true;

            this.$api.login({
                data: {
                    grant_type: 'password',
                    username: this.$refs.username.value,
                    password: this.$refs.password.value,
                    code: Object.prototype.hasOwnProperty.call(this.$refs, "code") ? this.$refs.code.value : null,
                }
            }).then(({data}) => {
                this.failedAttempts = 0;
                AuthService.completeAuthentication(data);
            }).catch(({data, error}) => {
                
                // Did the login fail because a 2FA code is required? 
                if (data !== undefined && data !== null) {
                    if (Object.prototype.hasOwnProperty.call(data, "_2fa_enabled") === true) {
                        if (data._2fa_enabled === 1) {
                            this.showFACode = true;
                            return; // Don't count this as a failed attempt
                        }
                    }
                }

                // Other error
                if (error !== undefined && error !== null && error.status === 418) {
                    const token = data.token ?? '';
                    const email = data.email ?? '-';
                    let routePath = this.$enums.RoutePathEnum.ACCOUNT_CONFLICT;
                    routePath = routePath.replace(':token', token);
                    routePath = routePath.replace(':email', email);
                    this.$router.push(routePath);
                    return; // Don't count this as a failed attempt
                }

                if (AuthService.deliverAuthFailureToOpener()) {
                    return;
                }

                this.failedAttempts++;
                this.loginError = true;
                this.loginErrorMessage = 'Invalid account or password. Click the eye icon to reveal password.';

                if (this.failedAttempts >= this.maxFailedAttempts) {
                    this.actionForgotPassword();
                }

            }).finally(() => {
                this.loadingState = false;
            });

        },
        actionForgotPassword() {
            this.$router.push(this.$enums.RoutePathEnum.FORGOT_PASSWORD);
        },
        actionFederatedEmailLogin() {
            this.$router.push(this.$enums.RoutePathEnum.FEDERATED_EMAIL_LOGIN);
        },
        ...mapMutations('app', {
            setGlobalLoadingState: 'setGlobalLoadingState',
        })
    },
    mounted() {
        if (AuthService.handleAlreadyAuthenticatedSession()) {
            return;
        }

        if (AuthService.isLogged()) {
            this.$router.push(this.$enums.RoutePathEnum.DASHBOARD);
        }

        let confirmEmailToken = this.$route.query.confirmEmailToken ?? '';

        if (confirmEmailToken.length > 0) {
            this.setGlobalLoadingState(true);
            this.$api.confirmEmail({
                data: {
                    token: confirmEmailToken
                }
            }).then(({data}) => {
                if (AuthService.completeAuthentication(data)) {
                    this.setGlobalLoadingState(false);
                    return;
                }

                setTimeout(() => {
                    this.setGlobalLoadingState(false);
                    this.$router.push(this.$enums.RoutePathEnum.DASHBOARD);
                }, 10);
            }).finally(() => {
                this.setGlobalLoadingState(false);
                this.$router.push(this.$enums.RoutePathEnum.LOGIN)
            });
        } else {
            this.setGlobalLoadingState(false);
        }
    }
}
</script>

<style scoped>
.login-page {
    width: 100%;
    min-height: 100vh;
    box-sizing: border-box;
}

.sso-notice-below {
    width: 400px;
    max-width: calc(100vw - 32px);
    margin: 16px auto 0;
    box-sizing: border-box;
}

@media (max-width: 600px) {
    .sso-notice-below {
        max-width: calc(100vw - 24px);
    }
}
</style>
